// Command-center client — the Agreement Command Center wire contract.
//
// One vocabulary for every surface (design brief §1/§8/§9/§12):
//   thread     = one workflow instance (lease, renewal, CR, RV, intake, …)
//   submission = one trip through the signing spine (PDF version + signer roster)
//   signal     = one item of operational attention
//   phase      = the 7-value cross-workflow rollup; nativeState stays verbatim
//
// Served by apps/api/src/routes/commandCenter.ts.

import { api } from './api';
import type { AppfolioSyncDisplay } from './appfolioSync';
import type { CurrentDocumentType } from './leaseDraftsClient';
import type { DocListRow, DocWithheldNote } from './viewDocsClient';

// ---------- shared vocabulary ----------

export type Phase =
  | 'preparing'
  | 'ready'
  | 'out'
  | 'countersign'
  | 'done'
  | 'blocked'
  | 'closed';

export const PHASES: Phase[] = [
  'preparing',
  'ready',
  'out',
  'countersign',
  'done',
  'blocked',
  'closed',
];

export const PHASE_LABEL: Record<Phase, string> = {
  preparing: 'Preparing',
  ready: 'Ready',
  out: 'Out',
  countersign: 'Countersign',
  done: 'Done',
  blocked: 'Blocked',
  closed: 'Closed',
};

/** StatusChip tone per the brief's color law (§8). */
export function phaseTone(phase: Phase): 'blocked' | 'done' | 'closed' | 'active' {
  if (phase === 'blocked') return 'blocked';
  if (phase === 'done') return 'done';
  if (phase === 'closed') return 'closed';
  return 'active';
}

export type ThreadType =
  | 'lease'
  | 'renewal'
  | 'adoption_addendum'
  | 'payment_plan'
  | 'condition_report'
  | 'moveout_funds'
  | 'manual_document'
  | 'leaseholder_change'
  | 'leaseholder_removal'
  | 'animal_addendum'
  | 'animal_addendum_paid'
  | 'owner_agreement'
  | 'intake'
  | 'rental_verification'
  | 'occupancy_sync';

export type CaseKind = 'household' | 'owner' | 'standalone';

/**
 * ThreadType → the signature-record documentType backing it (the vocabulary of
 * the unified current-state download endpoint). Thread types with no document
 * (intake, rental_verification, occupancy_sync) map to null — their detail has
 * nothing to download. For every document-backed thread type, the thread's
 * nativeId (the part of `id` after the first ':') IS the record's groupId.
 */
export function docTypeForThread(type: ThreadType): CurrentDocumentType | null {
  switch (type) {
    case 'lease':
    case 'renewal':
      return 'lease';
    case 'adoption_addendum':
      return 'adoption_addendum';
    case 'payment_plan':
      return 'payment_plan';
    case 'condition_report':
      return 'condition_report';
    case 'moveout_funds':
      return 'moveout_funds_direction';
    case 'manual_document':
      return 'manual_document';
    case 'leaseholder_change':
      return 'leaseholder_change';
    case 'leaseholder_removal':
      return 'leaseholder_removal';
    case 'animal_addendum':
      return 'animal_addendum';
    case 'animal_addendum_paid':
      return 'animal_addendum_paid';
    case 'owner_agreement':
      return 'property_management_agreement';
    default:
      return null;
  }
}

/** The nativeId half of a thread id (`${type}:${nativeId}`; the nativeId may
 *  itself contain ':'). */
export function threadNativeId(id: string): string {
  return id.slice(id.indexOf(':') + 1);
}

export type SignalSeverity = 'block' | 'attention' | 'notice';

export const SEVERITY_LABEL: Record<SignalSeverity, string> = {
  block: 'Blocks',
  attention: 'Needs attention',
  notice: 'Notices',
};

// ---------- rows ----------

export interface SignatureProgress {
  signed: number;
  total: number;
  nextName: string | null;
  nextRole: string | null;
  /** 'running' | 'exhausted' | 'off' — reminder-ladder state, when known. */
  reminders: string | null;
}

export interface SignalCounts {
  block: number;
  attention: number;
  notice: number;
}

export interface ThreadRow {
  /** Stable id: `${type}:${nativeId}` — also the thread-detail URL key. */
  id: string;
  type: ThreadType;
  typeLabel: string;
  caseId: string | null;
  caseKind: CaseKind;
  /** Who/where — household or owner display name. */
  subject: string;
  propertyName: string | null;
  unitName: string | null;
  phase: Phase;
  /** The workflow's own state, verbatim (the record; phase is the rollup). */
  nativeState: string;
  nativeStateLabel: string;
  signature: SignatureProgress | null;
  createdAt: string | null;
  updatedAt: string | null;
  ageDays: number | null;
  signals: SignalCounts;
  /** Go-forward cutoff honesty: record exists but was intentionally never sent. */
  neverSent: boolean;
  /** DURABLE case identity (Phase 3): set only for case-member groups
   *  (go-forward — post-deploy sends/renewals). Distinct from `caseId` (the
   *  per-group case-file navigation key). */
  durableCaseId?: string | null;
  /** Display label for the durable case ("Smith household"). */
  durableCaseLabel?: string | null;
}

/** Staff resolution state layered on a detected signal (ack / snooze / assign). */
export interface SignalResolution {
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  snoozeUntil: string | null;
  /** True while snoozeUntil is still in the future — grayed + uncounted. */
  snoozed: boolean;
  assignedTo: string | null;
  assignedBy: string | null;
  note: string | null;
}

export interface Signal {
  id: string;
  severity: SignalSeverity;
  category: string;
  categoryLabel: string;
  message: string;
  objectType: 'case' | 'thread' | 'submission';
  caseId: string | null;
  threadId: string | null;
  source: string;
  since: string | null;
  ageDays: number | null;
  /** SPA route where this can be dealt with. */
  href: string;
  /** Deterministic resolution-store id — the key the ack/snooze/assign/clear
   *  calls take. Stable across snapshot rebuilds (never derived from the
   *  message, whose embedded ages change daily). */
  resolutionId: string;
  resolution: SignalResolution | null;
}

/** Actively resolved — acknowledged, or snoozed and not yet unsealed. Such a
 *  signal renders grayed and is excluded from every severity count. */
export function isSignalResolved(s: Pick<Signal, 'resolution'>): boolean {
  const r = s.resolution;
  return !!r && (r.acknowledgedAt != null || r.snoozed);
}

export interface SubmissionSigner {
  name: string;
  role: string;
  email: string | null;
  orderGroup: number;
  status: string;
  sentAt: string | null;
  openedAt: string | null;
  completedAt: string | null;
  declinedAt: string | null;
  /** 'Co-signer' | 'Guarantor' when the signer matches an applicant whose
   *  AppFolio Type says so; null = plain applicant / WHP signer / unknown. */
  applicantType: string | null;
}

export interface SubmissionRow {
  submissionKey: string;
  groupId: string;
  /** The generated-PDF version this submission carries (lease-draft docs). */
  versionId: string;
  docusealSubmissionId: number | null;
  hasSignedPdf: boolean;
  hasAuditCert: boolean;
  documentType: string;
  documentLabel: string;
  status: string;
  statusLabel: string;
  subject: string;
  propertyName: string | null;
  signedCount: number;
  totalCount: number;
  nextName: string | null;
  nextRole: string | null;
  waitingOnWhp: boolean;
  daysWaiting: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  signers: SubmissionSigner[];
  supersededBy: string | null;
  threadId: string | null;
  caseId: string | null;
  /** Condition reports only — this submission is a corrected version, and where it stands. */
  correction?: 'pending' | 'took_effect' | 'withdrawn';
}

// ---------- overview ----------

export interface AttentionBucket {
  count: number;
  oldestDays: number | null;
  byCategory: { category: string; label: string; count: number }[];
}

export interface LaneSummary {
  type: ThreadType;
  label: string;
  phases: Partial<Record<Phase, number>>;
  total: number;
}

export interface MovementEvent {
  at: string;
  text: string;
  href: string | null;
}

export interface OverviewPayload {
  attention: {
    block: AttentionBucket;
    attention: AttentionBucket;
    notice: AttentionBucket;
  };
  outForSignature: SubmissionRow[];
  movement: MovementEvent[];
  generatedAt: string;
}

// ---------- case file ----------

export interface CasePerson {
  name: string;
  isLead: boolean;
  /** applicant | co-signer | guarantor | occupant | tenant — orthogonal to lead. */
  type: string;
  email: string | null;
  isMinor: boolean;
  comingOfAge: string | null;
}

export interface CaseFact {
  label: string;
  value: string;
  source: 'appfolio' | 'staff' | 'signer' | 'derived';
  note: string | null;
}

export interface CaseAction {
  id: string;
  label: string;
  enabled: boolean;
  blockedReason: string | null;
  href: string | null;
}

export interface ActivityEvent {
  at: string;
  text: string;
}

/** Durable-case panel data (Phase 3 item 5): the whole case this group belongs
 *  to — every member group across renewals + every thread in the case. */
export interface DurableCaseMember {
  groupId: string;
  linkedVia: string;
  linkedAt: string | null;
  isCurrent: boolean;
  isThisGroup: boolean;
}

export interface DurableCasePayload {
  caseId: string;
  householdName: string | null;
  unitId: string;
  currentGroupId: string | null;
  startedAt: string | null;
  lastLeaseAt: string | null;
  members: DurableCaseMember[];
  threads: ThreadRow[];
}

/** One pre-lease intake section's state on the case file, with the in-place
 *  action key: `resendId` feeds the existing POST /api/requests/resend. */
export interface CaseIntakeSection {
  section: 'designation' | 'yardCare' | 'animals';
  kind: 'intake_designation' | 'intake_yard_care' | 'intake_animals';
  label: string;
  status:
    | 'not_requested'
    | 'requested'
    | 'completed'
    | 'cancelled'
    | 'superseded'
    | 'not_applicable';
  statusLabel: string;
  requestedAt: string | null;
  completedAt: string | null;
  resendId: string | null;
}

/** Case-file intake block (household cases): section states + whether the
 *  "Request intake" action (POST /api/cc/case/:caseId/request-intake) applies. */
export interface CaseIntake {
  exists: boolean;
  sections: CaseIntakeSection[];
  canRequest: boolean;
  requestNote: string | null;
}

export interface CaseFilePayload {
  caseId: string;
  kind: CaseKind;
  name: string;
  propertyName: string | null;
  unitName: string | null;
  afStatus: string | null;
  afStatusLabel: string | null;
  converted: boolean;
  freshAsOf: string | null;
  people: CasePerson[];
  facts: CaseFact[];
  threads: ThreadRow[];
  actions: CaseAction[];
  signals: Signal[];
  activity: ActivityEvent[];
  /** Present (non-null) only when this group is a durable-case member
   *  (household cases, go-forward). Owner cases never carry it. */
  durableCase?: DurableCasePayload | null;
  /** Intake state + in-place actions — household cases only. */
  intake?: CaseIntake | null;
  /**
   * WEL-144 — every document this household has ever been sent, in the SAME row
   * shape `/api/docs` returns, so the household file and the Docs list can never
   * disagree about what exists. Optional for backward-compat with an API that
   * predates it; a docket failure degrades to empty rather than failing the file.
   */
  docket?: HouseholdDocket | null;
}

/** The household's complete document list (API: lib/commandCenter/docket.ts). */
export interface HouseholdDocket {
  rows: DocListRow[];
  /** Kinds found in this household's storage with no registry entry. */
  gaps?: Array<{ kind: string; count: number }>;
  /** Kinds withheld from THIS viewer — stated, never silently omitted. */
  withheld?: DocWithheldNote[];
}

// ---------- thread detail ----------

export interface RailStep {
  state: string;
  label: string;
  status: 'past' | 'current' | 'future' | 'blocked';
}

export interface ThreadAction {
  id: string;
  label: string;
  kind: 'link' | 'builder';
  href: string;
}

export interface ThreadDetailPayload {
  thread: ThreadRow;
  rail: RailStep[];
  submissions: SubmissionRow[];
  actions: ThreadAction[];
  signals: Signal[];
  activity: ActivityEvent[];
  /** "Part of Case X" (Phase 3): present when the thread's group is a durable
   *  case member. `caseFileGroupId` targets the case's current group's file. */
  durableCase?: { caseId: string; label: string; caseFileGroupId: string } | null;
  /** AppFolio occupancy-sync state for this thread's document (the same DTO the
   *  Requests dashboard chip renders); null when no upload row applies. */
  appfolioSync?: AppfolioSyncDisplay | null;
}

// ---------- registry / list payloads ----------

export interface ThreadsPayload {
  threads: ThreadRow[];
  generatedAt: string;
}

export interface SubmissionsPayload {
  submissions: SubmissionRow[];
  generatedAt: string;
}

export interface SignalsPayload {
  signals: Signal[];
  generatedAt: string;
}

export interface SignalsSummary {
  block: number;
  attention: number;
  notice: number;
}

export interface HouseholdRow {
  caseId: string;
  name: string;
  propertyName: string | null;
  unitName: string | null;
  afStatusLabel: string | null;
  /** Raw AppFolio application status behind the label (optional on the wire). */
  afStatus?: string | null;
  converted: boolean;
  people: number;
  activeThreads: number;
  signals: SignalCounts;
  updatedAt: string | null;
  /**
   * OPEN — live work. CLOSED — converted, denied, cancelled, or aged past the
   * active window. Closed households are IN the list (their files still hold
   * every document we sent them, and an operator has to be able to reach one to
   * re-send it) but are behind the Show filter and carry the state on the row.
   * Optional for backward-compat with an API that predates it — treated as open.
   */
  state?: 'open' | 'closed';
}

export interface HouseholdsPayload {
  households: HouseholdRow[];
  generatedAt: string;
}

export interface OwnerEngagementRow {
  caseId: string;
  ownerNames: string;
  propertyAddress: string | null;
  phase: Phase;
  nativeStateLabel: string;
  signature: SignatureProgress | null;
  updatedAt: string | null;
  ageDays: number | null;
  /** Pre-agreement intake still awaiting the owner → staff can resend the invite
   *  (POST /api/owner-intake/:groupId/resend). Gates the OwnersPage Resend button. */
  canResendIntake: boolean;
}

export interface OwnersPayload {
  engagements: OwnerEngagementRow[];
  generatedAt: string;
}

export interface SearchResult {
  kind: 'household' | 'owner' | 'property' | 'thread' | 'person';
  label: string;
  sub: string | null;
  href: string;
}

export interface SearchPayload {
  results: SearchResult[];
}

// ---------- calls ----------

export function fetchOverview(): Promise<OverviewPayload> {
  return api.get<OverviewPayload>('/api/cc/overview');
}

export function fetchThreads(params?: {
  type?: string;
  phase?: string;
  caseId?: string;
  includeClosed?: boolean;
}): Promise<ThreadsPayload> {
  const q = new URLSearchParams();
  if (params?.type) q.set('type', params.type);
  if (params?.phase) q.set('phase', params.phase);
  if (params?.caseId) q.set('caseId', params.caseId);
  if (params?.includeClosed) q.set('includeClosed', '1');
  const qs = q.toString();
  return api.get<ThreadsPayload>(`/api/cc/threads${qs ? `?${qs}` : ''}`);
}

export function fetchSubmissions(params?: {
  status?: 'open' | 'all';
}): Promise<SubmissionsPayload> {
  const qs = params?.status ? `?status=${params.status}` : '';
  return api.get<SubmissionsPayload>(`/api/cc/submissions${qs}`);
}

export function fetchSignals(params?: {
  severity?: string;
  category?: string;
}): Promise<SignalsPayload> {
  const q = new URLSearchParams();
  if (params?.severity) q.set('severity', params.severity);
  if (params?.category) q.set('category', params.category);
  const qs = q.toString();
  return api.get<SignalsPayload>(`/api/cc/signals${qs ? `?${qs}` : ''}`);
}

export function fetchSignalsSummary(): Promise<SignalsSummary> {
  return api.get<SignalsSummary>('/api/cc/signals/summary');
}

export function fetchHouseholds(): Promise<HouseholdsPayload> {
  return api.get<HouseholdsPayload>('/api/cc/households');
}

export function fetchOwners(): Promise<OwnersPayload> {
  return api.get<OwnersPayload>('/api/cc/owners');
}

export function fetchCaseFile(caseId: string): Promise<CaseFilePayload> {
  return api.get<CaseFilePayload>(`/api/cc/case/${encodeURIComponent(caseId)}`);
}

export function fetchThreadDetail(threadId: string): Promise<ThreadDetailPayload> {
  // The id is opaque to the client — the server parses it.
  return api.get<ThreadDetailPayload>(`/api/cc/thread/${encodeURIComponent(threadId)}`);
}

export function searchAll(q: string): Promise<SearchPayload> {
  return api.get<SearchPayload>(`/api/cc/search?q=${encodeURIComponent(q)}`);
}

// ---------- case-file intake request (C) ----------

/** Counts from the targeted intake sweep the request-intake action runs. */
export interface RequestIntakeResult {
  scanned: number;
  created: number;
  requested: number;
  emailed: number;
}

/**
 * Request the pre-lease intake for a case's group without leaving the case
 * view — runs the existing intake-watch machinery targeted at this group
 * (idempotent; only sections whose trigger is due are requested/emailed).
 * `requested === 0` means nothing new became requestable.
 */
export function requestCaseIntake(caseId: string): Promise<RequestIntakeResult> {
  return api.post<RequestIntakeResult>(
    `/api/cc/case/${encodeURIComponent(caseId)}/request-intake`,
  );
}

// ---------- signal resolutions ----------

export interface SignalResolutionPayload {
  resolution: SignalResolution;
}

/** Acknowledge — "I've handled this". Grayed while the signal stays detected. */
export function ackSignal(resolutionId: string, note?: string): Promise<SignalResolutionPayload> {
  return api.post<SignalResolutionPayload>('/api/cc/signals/ack', {
    signalId: resolutionId,
    ...(note ? { note } : {}),
  });
}

/** Snooze until an ISO instant (grayed, unseals at the date); null un-snoozes. */
export function snoozeSignal(
  resolutionId: string,
  until: string | null,
  note?: string,
): Promise<SignalResolutionPayload> {
  return api.post<SignalResolutionPayload>('/api/cc/signals/snooze', {
    signalId: resolutionId,
    until,
    ...(note ? { note } : {}),
  });
}

/** Advisory assignment (operator id; no notifications); null unassigns. */
export function assignSignal(
  resolutionId: string,
  assignedTo: string | null,
): Promise<SignalResolutionPayload> {
  return api.post<SignalResolutionPayload>('/api/cc/signals/assign', {
    signalId: resolutionId,
    assignedTo,
  });
}

/** Remove the resolution entirely — the undo for ack/snooze/assign. */
export function clearSignalResolution(resolutionId: string): Promise<{ cleared: boolean }> {
  return api.post<{ cleared: boolean }>('/api/cc/signals/clear', { signalId: resolutionId });
}

// ---------- quiet document swap (Phase 3 item 4) ----------

export interface QuietSwapSuccess {
  ok: true;
  groupId: string;
  documentType: string;
  oldSubmissionId: number;
  newSubmissionId: number;
  newVersionId: string;
  /** Set when the swap succeeded but the OLD submission could not be archived
   *  remotely — staff should cancel it from the thread. */
  warning?: string;
}

/**
 * Silently replace the thread's current out-for-signature submission with a
 * re-produced document (zero completed signers required — the server enforces
 * every guard and 409s otherwise). Signers keep their exact links + reminder
 * schedule. Non-2xx throws ApiError with `{ error, code }` in the body.
 */
export function quietSwapThread(
  threadId: string,
  args: { submissionId: number; reason?: string },
): Promise<QuietSwapSuccess> {
  return api.post<QuietSwapSuccess>(
    `/api/cc/thread/${encodeURIComponent(threadId)}/quiet-swap`,
    args,
  );
}

// ---------- condition report: corrected version (2026-10-06) ----------

export interface CorrectionResult {
  ok: true;
  correction: {
    status: 'pending' | 'withdraw_unknown' | 'took_effect' | 'withdrawn';
    requestedAt: string;
    requestedBy: string;
    whatChanged?: string;
    tookEffectAt?: string;
    withdrawnAt?: string;
  };
  /** Residents emailed the corrected-version request by this call. */
  emailed?: number;
  /** True when this call finished an earlier, interrupted request. */
  resumed?: boolean;
}

/**
 * "Request new signatures" — send a corrected version of an open condition
 * report. It replaces the first only once every resident signs it. `endpoint`
 * comes from the item payload (`conditionReportCorrection.requestEndpoint`).
 * Non-2xx throws ApiError with `{ error, code }`.
 */
export function requestConditionReportNewSignatures(
  endpoint: string,
  args: { whatChanged?: string },
): Promise<CorrectionResult> {
  return api.post<CorrectionResult>(endpoint, args.whatChanged ? { whatChanged: args.whatChanged } : {});
}

/**
 * Resolve a withdrawal DocuSeal couldn't confirm (`withdraw_unknown`): staff
 * checked DocuSeal and record what they found. `endpoint` is the payload's
 * `conditionReportCorrection.resolveWithdrawalEndpoint`.
 */
export function resolveConditionReportWithdrawal(
  endpoint: string,
  outcome: 'withdrawn' | 'pending',
): Promise<CorrectionResult> {
  return api.post<CorrectionResult>(endpoint, { outcome });
}

/** Withdraw the pending corrected version; the first version stands. */
export function withdrawConditionReportCorrection(endpoint: string): Promise<CorrectionResult> {
  return api.post<CorrectionResult>(endpoint, {});
}

// ---------- display helpers ----------

export function formatAge(days: number | null): string {
  if (days == null) return '—';
  if (days < 1) return 'today';
  const d = Math.floor(days);
  return d === 1 ? '1 day' : `${d} days`;
}

export function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  }
  // The time stays on older rows too — "Aug 28" alone can't say whether the
  // reminder went out before or after the applicant opened the page
  // (Miles, 2026-08-29: "timestamps are helpful").
  return `${d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}`;
}
